import { FC, PropsWithChildren, ReactNode } from "react";

/**
 * One titled block of the order page — Main Info, Billing Info and so on.
 *
 * The old page drew these as `<h3>` over a striped table; same shape, same
 * reading order, just not in tables that are also doing the layout.
 */
export const DetailPanel: FC<PropsWithChildren<{
  title: string,
  className?: string,
  actions?: ReactNode,
}>> = ({ title, className = '', actions, children }) => (
  <section className={`bg-white rounded-lg shadow-sm ${className}`}>
    <div className="flex items-center justify-between gap-4 px-5 pt-4 pb-2 border-b-[1px] border-border_color">
      <h3 className="font-semibold text-md">{title}</h3>
      {actions}
    </div>
    <div className="px-5 py-4">{children}</div>
  </section>
);

/**
 * A label/value pair.
 *
 * Plain text. These used to render in a read-only box, copying the old page's
 * `<input readonly>`, back when the page could not save anything. Editing now
 * happens in its own dialog, so a box here only looked like a field that
 * ought to accept typing and did not.
 */
export const DetailRow: FC<{
  label: string,
  value?: ReactNode,
}> = ({ label, value }) => {
  const empty = value === null || value === undefined || value === '';

  return (
    <div className="flex gap-3 py-[5px] odd:bg-gray-50 -mx-2 px-2 rounded">
      <div className="w-[190px] shrink-0 text-text_pale">{label}</div>
      <div className="grow min-w-0 break-words">
        {empty ? <span className="text-gray-300">—</span> : value}
      </div>
    </div>
  );
};

/** A plain table for the page's list blocks: refunds, items, replacements. */
export const DetailTable: FC<PropsWithChildren<{
  head: string[],
  empty?: string,
  /**
   * Vertical rules between the cells.
   *
   * Opt-in, so the other detail tables keep the plain look they have. The
   * caller styles its own `td`s to match - this component only owns the
   * header row.
   */
  bordered?: boolean,
}>> = ({
  head,
  empty = 'None',
  bordered = false,
  children,
}) => {
  const hasRows = Array.isArray(children) ? children.flat().some(Boolean) : !!children;

  if (!hasRows)
    return <div className="text-text_pale py-2">{empty}</div>;

  return (
    <div className="overflow-x-auto stylledScroll">
      <table className="min-w-full border-separate border-spacing-0">
        <thead>
          <tr>
            {head.map((title) => (
              <th
                key={title}
                scope="col"
                className={`
                  text-left font-bold whitespace-nowrap py-2 px-2
                  border-b-[1px] border-b-gray-300
                  ${bordered ? 'border-x-[1px] border-x-gray-200 first:border-l-0 last:border-r-0' : ''}
                `}
              >
                {title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
};
